import { useApolloClient } from '@apollo/client';
import { message, Upload, type UploadProps } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import getApolloErrors from '../../../../server/utils/getApolloErrors';
import { type LanguagePackSpecsFragment } from '../../../api/fragments/LanguagePackSpecs';
import {
    ImportLanguagePackDocument,
    type ImportLanguagePackMutation,
    type ImportLanguagePackMutationVariables,
} from '../../../api/mutations/importLanguagePack';
import { CompanyTheme } from '../../../api/types';
import CancelButton from '../../../components/modal/CancelButton';
import OkButton from '../../../components/modal/OkButton';
import { useThemeComponents } from '../../../themes';
import { allowedExtensions, validateExtensions } from '../../../utilities/extensions';

type LanguagePackModalProps = {
    isOpen: boolean;
    languagePackId: string;
    onClose: () => void;
    onChange: (newValue: LanguagePackSpecsFragment['translations']) => void;
};

const LanguagePackModal = ({ onClose, onChange, languagePackId, isOpen }: LanguagePackModalProps) => {
    const { t } = useTranslation(['languagePackDetails', 'importExportExcel']);
    const apolloClient = useApolloClient();
    const { theme, Modal } = useThemeComponents();

    const onUpload = useCallback(
        async inputFile => {
            try {
                // submitting message
                message.loading({
                    content: t('languagePackDetails:messages.importing'),
                    key: 'primary',
                    duration: 0,
                });

                const { data } = await apolloClient.mutate<
                    ImportLanguagePackMutation,
                    ImportLanguagePackMutationVariables
                >({
                    mutation: ImportLanguagePackDocument,
                    variables: {
                        upload: inputFile,
                        languagePackId,
                    },
                });

                onChange(data.language.translations);

                // inform about success
                message.success({
                    content: t('languagePackDetails:messages.importSuccessful'),
                    key: 'primary',
                });

                onClose();
            } catch (error) {
                const apolloErrors = getApolloErrors(error);
                console.error(apolloErrors);

                message.error({
                    content: t('languagePackDetails:messages.importFailed'),
                    key: 'primary',
                });
            }
        },
        [t, apolloClient, languagePackId, onChange, onClose]
    );

    const handleOnChange = useCallback(
        ({ file }) => {
            if (file instanceof File) {
                onUpload(file);
            }
        },
        [onUpload]
    );
    const beforeUpload = useCallback<NonNullable<UploadProps['beforeUpload']>>(
        file => {
            const reader = new FileReader();
            const promise = new Promise<boolean | string>((resolve, reject) => {
                reader.onload = async event => {
                    const content = event.target.result as string;
                    const validationResult = await validateExtensions(content, file.name);
                    if (validationResult.valid) {
                        return resolve(false);
                    }
                    message.error(t('common:upload.messages.fileError'));

                    return resolve(Upload.LIST_IGNORE);
                };

                reader.onerror = event => resolve(Upload.LIST_IGNORE);
            });

            reader.readAsBinaryString(file);

            return Promise.resolve(promise);
        },
        [t]
    );

    const footer = useMemo(() => {
        const cancelButton = (
            <CancelButton key="cancel" onClick={onClose} style={{ marginRight: '15px' }}>
                {t('languagePackDetails:actions.cancel')}
            </CancelButton>
        );

        const okButton = (
            <Upload
                accept={allowedExtensions.excel.join(',')}
                beforeUpload={beforeUpload}
                onChange={handleOnChange}
                showUploadList={false}
            >
                <OkButton key="submit">{t('languagePackDetails:actions.import')}</OkButton>
            </Upload>
        );

        if (theme === CompanyTheme.PorscheV3 || theme === CompanyTheme.Porsche) {
            return [okButton, cancelButton];
        }

        return [cancelButton, okButton];
    }, [onClose, beforeUpload, handleOnChange, theme, t]);

    return (
        <Modal footer={footer} onCancel={onClose} open={isOpen} title={t('languagePackDetails:import.title')}>
            {t('importExportExcel:description.message')}
        </Modal>
    );
};

const useLanguagePackModal = (languagePackId: string) => {
    const [isModalOpen, setIsModalOpen] = useState(false);

    const actions = useMemo(() => {
        const close = () => setIsModalOpen(false);
        const open = () => setIsModalOpen(true);

        return {
            open,
            close,
        };
    }, []);

    return {
        ...actions,
        render: ({ onChange }: { onChange: LanguagePackModalProps['onChange'] }) => (
            <LanguagePackModal
                isOpen={isModalOpen}
                languagePackId={languagePackId}
                onChange={onChange}
                onClose={actions.close}
            />
        ),
    };
};

export default useLanguagePackModal;
